import React, {useState, useRef} from "react";
import "./Register.scss";
import * as Yup from "yup";
import {Field, Form, Formik} from "formik";
import useFetch, {baseUrl} from "../../../hooks/useFetch";
import usePost from "../../../hooks/usePost";
import "react-toastify/dist/ReactToastify.css";
import {country} from "../../../utils/helpers/country";
import {toast} from "react-toastify";

const RegisterSchema = Yup.object().shape({
  firstname: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
  lastname: Yup.string().min(2, "Too Short!").max(50, "Too Long!").required("Required"),
  email: Yup.string().email("Invalid email").required("Required"),
  country: Yup.string().required("Required"),
  event: Yup.string().required("Please select an event"),
});

function Register() {
  const initialValues = {
    firstname: "",
    lastname: "",
    email: "",
    country: "",
    event: "",
  };
  const [searchQuery, setSearchQuery] = useState("");

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value); // Update search query
  };

  const filteredCountries = country.filter((item) => item.country.toLowerCase().includes(searchQuery.toLowerCase()));

  const {isLoading, data, error, postData} = usePost(`${baseUrl}web/register-event/`);
  const {isLoading: loadingEvent, serverError, apiData} = useFetch(`${baseUrl}web/get-events/?status=upcoming`);

  const handleSubmit = async (values, {setSubmitting, resetForm}) => {
    try {
      const payload = {
        first_name: values.firstname,
        last_name: values.lastname,
        email: values.email,
        country: values.country,
        event: values.event,
      };
      const res = await postData(payload, "Registered Successfully");
      resetForm();
      setSelectedCountry("");
      setSearchQuery("");
      setSelectedEvent("");
    } catch (err) {
      toast.error(err);
      console.error("Error during registration", err);
    }
  };
  const [isDropdownOpenEvent, setIsDropdownOpenEvent] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState("");
  const dropdownEventRef = useRef();

  const handleDropdownToggleEvent = () => {
    setIsDropdownOpenEvent(!isDropdownOpenEvent);
  };

  const handleEventSelect = (countryName) => {
    setSelectedEvent(countryName);
    setIsDropdownOpenEvent(false);
  };

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState("");
  const dropdownRef = useRef();

  const handleDropdownToggle = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleCountrySelect = (countryName) => {
    setSelectedCountry(countryName);
    setIsDropdownOpen(false);
  };

  const handleClickOutside = (e) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
      setIsDropdownOpen(false);
    }
    if (dropdownEventRef.current && !dropdownEventRef.current.contains(e.target)) {
      setIsDropdownOpenEvent(false);
    }
  };

  React.useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="rg_wrapper">
      <div className="rg_left">
        <h1>
          <span className="gradientText">Register</span> for Our Next Event
        </h1>
        <p>
          Don’t miss out on the opportunity to connect with top traders, analyst, and enthusiasts at our upcoming event. Secure your spot now and take a step toward expanding your network and
          enhancing your trading skills. Simply fill out the form below to be notified about our next event.
        </p>
      </div>
      <div className="rg_right">
        <Formik
          initialValues={initialValues}
          validationSchema={RegisterSchema}
          onSubmit={handleSubmit}
        >
          {({errors, touched, setFieldValue}) => (
            <Form>
              <div className="rg_form">
                <div className="input_wrapper">
                  <label>
                    First Name<sup>*</sup>
                  </label>
                  <div className="input_box">
                    <Field
                      name="firstname"
                      type="text"
                      placeholder="Enter Your First Name"
                    />
                  </div>
                  {errors.firstname && touched.firstname && <div className="errorMessage">{errors.firstname}</div>}
                </div>
                <div className="input_wrapper">
                  <label>
                    Last Name<sup>*</sup>
                  </label>
                  <div className="input_box">
                    <Field
                      name="lastname"
                      placeholder="Enter Your Last Name"
                      type="text"
                    />
                  </div>
                  {errors.lastname && touched.lastname && <div className="errorMessage">{errors.lastname}</div>}
                </div>
                <div className="input_wrapper">
                  <label>
                    Email<sup>*</sup>
                  </label>
                  <div className="input_box">
                    <Field
                      type="text"
                      name="email"
                      placeholder="Enter Your Email"
                    />
                  </div>
                  {errors.email && touched.email && <div className="errorMessage">{errors.email}</div>}
                </div>

                {/* Custom Country Dropdown */}
                <div className="input_wrapper">
                  <label>
                    Country<sup>*</sup>
                  </label>
                  <div
                    className="input_box"
                    ref={dropdownRef}
                  >
                    <input
                      type="text"
                      placeholder="Search for a country"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onClick={() => setIsDropdownOpen(true)}
                    />
                    {isDropdownOpen && (
                      <div className="dropdown-options">
                        {filteredCountries.map((item) => (
                          <div
                            key={item.iso}
                            className="dropdown-option"
                            onClick={() => {
                              handleCountrySelect(item.country);
                              setSearchQuery(item.country);
                              setFieldValue("country", item.country); // Update Formik field value
                            }}
                          >
                            {item.country}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.country && touched.country && <div className="errorMessage">{errors.country}</div>}
                </div>

                <div className="input_wrapper">
                  <label>
                    Event<sup>*</sup>
                  </label>
                  <div
                    className="input_box"
                    ref={dropdownEventRef}
                  >
                    <div
                      className="custom-dropdown"
                      onClick={handleDropdownToggleEvent}
                    >
                      {selectedEvent || "Select your country"}
                      <span className="arrow">{isDropdownOpenEvent ? "▲" : "▼"}</span>
                    </div>
                    {isDropdownOpenEvent && (
                      <div className="dropdown-options">
                        {apiData?.map((item) => (
                          <div
                            key={item.iso}
                            className="dropdown-option"
                            onClick={() => {
                              handleEventSelect(item.name);
                              setFieldValue("event", item.id);
                            }}
                          >
                            {item.name}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  {errors.event && touched.event && <div className="errorMessage">{errors.event}</div>}
                </div>

                {/* {error && <div className="errorMessage">{error.message || "An error occurred"}</div>}
              {data && !error && <div className="successMessage">Registration successful!</div>} */}
              </div>
              <button
                className="gradientButton_small"
                type="submit"
                disabled={isLoading}
                style={{marginTop: "20px"}}
              >
                {isLoading ? "Submitting..." : "Submit"}
              </button>
            </Form>
          )}
        </Formik>
      </div>
    </div>
  );
}

export default Register;
